import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Link, useParams } from '@tanstack/react-router'
import type { ValueListItemView } from '@messe/shared'
import { Button } from '../components/ui/button.js'
import { CheckboxField, TextField } from '../components/ui/field.js'
import { Alert, Badge } from '../components/ui/feedback.js'
import { ValueListItemDialog } from '../components/value-list-item-dialog.js'
import { MergeDialog } from '../components/merge-dialog.js'
import { CsvImportDialog } from '../components/csv-import-dialog.js'
import { format, t } from '../i18n/index.js'
import { ApiError } from '../lib/api.js'
import {
  SUPPLIER_METADATA_FIELDS,
  listValueListItems,
  listValueLists,
  updateValueListItem,
} from '../lib/value-lists.js'

const PAGE_SIZE = 25

type Dialog =
  | { kind: 'none' }
  | { kind: 'create' }
  | { kind: 'edit'; item: ValueListItemView }
  | { kind: 'merge'; item: ValueListItemView }
  | { kind: 'import' }

export function ValueListDetailPage() {
  const strings = t()
  const queryClient = useQueryClient()
  const { listId } = useParams({ from: '/authenticated/stammdaten/$listId' })

  const [search, setSearch] = useState('')
  const [includeArchived, setIncludeArchived] = useState(false)
  const [sort, setSort] = useState<'label' | 'value' | 'sortOrder' | 'createdAt'>('label')
  const [direction, setDirection] = useState<'asc' | 'desc'>('asc')
  const [page, setPage] = useState(0)
  const [dialog, setDialog] = useState<Dialog>({ kind: 'none' })
  const [notice, setNotice] = useState<string | null>(null)

  const lists = useQuery({ queryKey: ['value-lists'], queryFn: listValueLists })
  const list = lists.data?.find((entry) => entry.id === listId)
  const isSupplierList = list?.key === 'suppliers'

  const itemsQuery = useQuery({
    queryKey: ['value-list-items', listId, { search, includeArchived, sort, direction, page }],
    queryFn: () =>
      listValueListItems(listId, {
        search: search || undefined,
        activeOnly: !includeArchived,
        sort,
        direction,
        limit: PAGE_SIZE,
        offset: page * PAGE_SIZE,
      }),
  })

  const archive = useMutation({
    mutationFn: (item: ValueListItemView) =>
      updateValueListItem(listId, item.id, {
        status: item.status === 'archived' ? 'active' : 'archived',
      }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['value-list-items'] })
      await queryClient.invalidateQueries({ queryKey: ['value-lists'] })
    },
  })

  /** Clicking a column heading sorts by it, clicking again reverses. */
  function sortBy(field: typeof sort) {
    if (sort === field) {
      setDirection(direction === 'asc' ? 'desc' : 'asc')
    } else {
      setSort(field)
      setDirection('asc')
    }
    setPage(0)
  }

  const total = itemsQuery.data?.total ?? 0
  const pageCount = Math.max(1, Math.ceil(total / PAGE_SIZE))

  function metadataValue(item: ValueListItemView, key: string): string {
    const value = item.metadata[key]
    return typeof value === 'string' ? value : ''
  }

  return (
    <div className="flex flex-col gap-6">
      <div>
        <Link to="/stammdaten" className="text-accent text-sm hover:underline">
          ← {strings.valueLists.detail.back}
        </Link>
      </div>

      <header className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="text-xl font-semibold">{list?.name ?? strings.app.loading}</h1>
          {list?.description && <p className="text-text-muted mt-1 text-sm">{list.description}</p>}
        </div>
        <div className="flex gap-2">
          <Button variant="secondary" onClick={() => setDialog({ kind: 'import' })}>
            {strings.valueLists.actions.import}
          </Button>
          <Button onClick={() => setDialog({ kind: 'create' })}>
            {strings.valueLists.actions.create}
          </Button>
        </div>
      </header>

      {notice && <Alert tone="success">{notice}</Alert>}

      {archive.isError && (
        <Alert tone="danger">
          {archive.error instanceof ApiError
            ? (archive.error.problem.detail ?? strings.errors.unexpected)
            : strings.errors.unexpected}
        </Alert>
      )}

      <div className="flex flex-wrap items-end gap-4">
        <div className="w-72">
          <TextField
            label={strings.valueLists.detail.searchLabel}
            placeholder={strings.valueLists.detail.searchPlaceholder}
            type="search"
            value={search}
            onChange={(event) => {
              setSearch(event.target.value)
              setPage(0)
            }}
          />
        </div>
        <div className="pb-2.5">
          <CheckboxField
            label={strings.valueLists.detail.showArchived}
            checked={includeArchived}
            onChange={(event) => {
              setIncludeArchived(event.target.checked)
              setPage(0)
            }}
          />
        </div>
      </div>

      {itemsQuery.isError && <Alert tone="danger">{strings.valueLists.loadError}</Alert>}
      {itemsQuery.isPending && <p className="text-text-muted text-sm">{strings.app.loading}</p>}

      {itemsQuery.data && (
        <>
          <p className="text-text-muted text-sm">
            {total === 1
              ? strings.valueLists.detail.countOne
              : format(strings.valueLists.detail.countMany, { count: total })}
          </p>

          {itemsQuery.data.items.length === 0 ? (
            <p className="text-text-muted text-sm">{strings.valueLists.detail.empty}</p>
          ) : (
            <div className="border-border overflow-x-auto rounded-lg border">
              <table className="w-full border-collapse text-sm">
                <thead className="bg-surface-sunken text-text-muted">
                  <tr>
                    <th scope="col" className="px-4 py-2.5 text-left font-medium">
                      <button
                        type="button"
                        onClick={() => sortBy('label')}
                        className="hover:underline"
                      >
                        {strings.valueLists.columns.label}
                        {sort === 'label' && (direction === 'asc' ? ' ↑' : ' ↓')}
                      </button>
                    </th>
                    <th scope="col" className="px-4 py-2.5 text-left font-medium">
                      <button
                        type="button"
                        onClick={() => sortBy('value')}
                        className="hover:underline"
                      >
                        {strings.valueLists.columns.value}
                        {sort === 'value' && (direction === 'asc' ? ' ↑' : ' ↓')}
                      </button>
                    </th>

                    {isSupplierList &&
                      SUPPLIER_METADATA_FIELDS.map((field) => (
                        <th
                          key={field.key}
                          scope="col"
                          className="px-4 py-2.5 text-left font-medium"
                        >
                          {strings.valueLists.columns[field.labelKey]}
                        </th>
                      ))}

                    <th scope="col" className="px-4 py-2.5 text-left font-medium">
                      {strings.valueLists.columns.status}
                    </th>
                    <th scope="col" className="px-4 py-2.5 text-right font-medium">
                      {strings.valueLists.columns.usage}
                    </th>
                    <th scope="col" className="px-4 py-2.5 text-right font-medium">
                      {strings.valueLists.columns.actions}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {itemsQuery.data.items.map((item) => (
                    <tr key={item.id} className="border-border border-t">
                      <td className="px-4 py-2.5 font-medium">{item.label}</td>
                      <td className="text-text-muted px-4 py-2.5">
                        <code className="text-xs">{item.value}</code>
                      </td>

                      {isSupplierList &&
                        SUPPLIER_METADATA_FIELDS.map((field) => (
                          <td key={field.key} className="text-text-muted px-4 py-2.5">
                            {metadataValue(item, field.key)}
                          </td>
                        ))}

                      <td className="px-4 py-2.5">
                        {item.status === 'active' && (
                          <Badge tone="success">{strings.valueLists.status.active}</Badge>
                        )}
                        {item.status === 'pending_approval' && (
                          <Badge tone="warning">{strings.valueLists.status.pending_approval}</Badge>
                        )}
                        {item.status === 'merged' && (
                          <span title={item.mergedIntoLabel ?? undefined}>
                            <Badge tone="info">{strings.valueLists.status.merged}</Badge>
                          </span>
                        )}
                        {item.status === 'archived' && (
                          <Badge tone="warning">{strings.valueLists.status.archived}</Badge>
                        )}
                      </td>

                      <td className="text-text-muted px-4 py-2.5 text-right">{item.usageCount}</td>

                      <td className="px-4 py-2.5">
                        <div className="flex justify-end gap-1">
                          {item.status !== 'merged' && (
                            <>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => setDialog({ kind: 'edit', item })}
                              >
                                {strings.valueLists.actions.edit}
                              </Button>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => setDialog({ kind: 'merge', item })}
                              >
                                {strings.valueLists.actions.merge}
                              </Button>
                              <Button
                                variant="ghost"
                                size="sm"
                                disabled={archive.isPending}
                                onClick={() => {
                                  setNotice(null)
                                  archive.mutate(item)
                                }}
                              >
                                {item.status === 'archived'
                                  ? strings.valueLists.actions.restore
                                  : strings.valueLists.actions.archive}
                              </Button>
                            </>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {pageCount > 1 && (
            <div className="flex items-center gap-3">
              <Button
                variant="secondary"
                size="sm"
                disabled={page === 0}
                onClick={() => setPage((current) => current - 1)}
              >
                {strings.valueLists.detail.previous}
              </Button>
              <span className="text-text-muted text-sm">
                {format(strings.valueLists.detail.page, {
                  page: page + 1,
                  pages: pageCount,
                })}
              </span>
              <Button
                variant="secondary"
                size="sm"
                disabled={page + 1 >= pageCount}
                onClick={() => setPage((current) => current + 1)}
              >
                {strings.valueLists.detail.next}
              </Button>
            </div>
          )}
        </>
      )}

      <ValueListItemDialog
        open={dialog.kind === 'create' || dialog.kind === 'edit'}
        listId={listId}
        isSupplierList={isSupplierList}
        item={dialog.kind === 'edit' ? dialog.item : null}
        onClose={() => setDialog({ kind: 'none' })}
        onSaved={(message) => {
          setNotice(message)
          setDialog({ kind: 'none' })
        }}
      />

      <MergeDialog
        open={dialog.kind === 'merge'}
        listId={listId}
        source={dialog.kind === 'merge' ? dialog.item : null}
        onClose={() => setDialog({ kind: 'none' })}
        onMerged={(message) => {
          setNotice(message)
          setDialog({ kind: 'none' })
        }}
      />

      <CsvImportDialog
        open={dialog.kind === 'import'}
        listId={listId}
        isSupplierList={isSupplierList}
        onClose={() => setDialog({ kind: 'none' })}
      />
    </div>
  )
}
